import { asSentence, type FlagReader, type SkippedClient } from './clientFlags';

/**
 * Why a group run leaves a client out:
 *   - `not-a-client`: no client config has the name, so there is nothing to
 *     deploy. An old saved group can hold one (`platform-settings`).
 *   - `deploy-block`: the client is marked do not deploy. A group has no override.
 *   - `inactive`: the client is marked inactive. A single deploy still works.
 */
export type GroupSkipKind = 'not-a-client' | 'deploy-block' | 'inactive';

/**
 * A client a group run left out, and why. The list saved for a run before
 * the kind was kept has none: every client on it was marked do not deploy.
 */
export interface GroupSkippedClient extends SkippedClient {
  kind?: GroupSkipKind;
}

/** The kind of a skip. A skip saved before kinds were kept was a do-not-deploy skip. */
export const skipKind = (skip: GroupSkippedClient): GroupSkipKind => skip.kind ?? 'deploy-block';

/**
 * Split a group run's clients into those it deploys to and those it skips.
 * Both lists keep the order of the input.
 *
 * `knownClients` is every client name that has a config. Null means the list
 * is not known yet: the "not a client" skip is then left out, and the caller
 * must not start a run on that answer.
 */
export function splitGroupClients(
  clients: readonly string[],
  flags: Pick<FlagReader, 'deployBlock' | 'inactive'>,
  knownClients: ReadonlySet<string> | null
): { allowed: string[]; skipped: GroupSkippedClient[] } {
  const allowed: string[] = [];
  const skipped: GroupSkippedClient[] = [];
  for (const clientName of clients) {
    if (knownClients && !knownClients.has(clientName)) {
      skipped.push({ clientName, kind: 'not-a-client', reason: 'Not a client', setBy: '' });
      continue;
    }
    const block = flags.deployBlock(clientName);
    if (block) {
      skipped.push({ clientName, kind: 'deploy-block', reason: block.reason, setBy: block.setBy, setAt: block.setAt });
      continue;
    }
    const inactive = flags.inactive(clientName);
    if (inactive) {
      skipped.push({ clientName, kind: 'inactive', reason: 'Inactive', setBy: inactive.setBy, setAt: inactive.setAt });
      continue;
    }
    allowed.push(clientName);
  }
  return { allowed, skipped };
}

const KIND_LABEL: Record<GroupSkipKind, string> = {
  'deploy-block': 'Marked do not deploy',
  inactive: 'Inactive',
  'not-a-client': 'Not a client',
};

const KIND_ORDER: GroupSkipKind[] = ['deploy-block', 'inactive', 'not-a-client'];

/** Why one client is skipped, as one sentence or two. */
export function skipLine(skip: GroupSkippedClient): string {
  switch (skipKind(skip)) {
    case 'deploy-block':
      return `Marked do not deploy. ${asSentence(skip.reason)}`;
    case 'inactive':
      return 'Marked inactive.';
    case 'not-a-client':
      return 'No client has this name, so there is nothing to deploy.';
  }
}

/**
 * One line that says which clients a run skips, grouped by why:
 * "3 clients are skipped. Marked do not deploy: a, b. Inactive: c."
 * Empty when nothing is skipped.
 */
export function describeSkips(skipped: readonly GroupSkippedClient[]): string {
  if (skipped.length === 0) return '';
  const parts = KIND_ORDER.map((kind) => {
    const names = skipped.filter((s) => skipKind(s) === kind).map((s) => s.clientName);
    return names.length > 0 ? `${KIND_LABEL[kind]}: ${names.join(', ')}.` : '';
  }).filter(Boolean);
  const lead = skipped.length === 1 ? '1 client is skipped.' : `${skipped.length} clients are skipped.`;
  return `${lead} ${parts.join(' ')}`;
}
